/**
 * Phone Number Prompt Modal Component
 *
 * Prompts authenticated users to provide a valid phone number when their
 * profile has a missing or placeholder value. Uses the existing Modal
 * component pattern (same as PasswordChangeModal).
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 3.1–3.4, 4.1–4.4
 */

import { useState, useEffect, useRef } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { useAuth } from '../../auth/AuthProvider';
import { restClient } from '../../api/rest-client';
import { validatePhoneForPrompt, formatPhoneForStorage } from '../../utils/phone';

interface PhoneNumberPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export default function PhoneNumberPromptModal({
  isOpen,
  onClose,
  onSaved,
}: PhoneNumberPromptModalProps) {
  const { user } = useAuth();
  const [phoneInput, setPhoneInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset form state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setPhoneInput('');
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Focus the input when the modal opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to let the modal render before focusing
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation
    const validation = validatePhoneForPrompt(phoneInput);
    if (!validation.isValid) {
      setError(validation.error ?? 'Invalid phone number');
      return;
    }

    if (!user) {
      setError('User session not found. Please try again.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const formattedPhone = formatPhoneForStorage(phoneInput);
      await restClient.updateUser(user.username, {
        username: user.username,
        email: user.email,
        phone: formattedPhone,
      });
      onSaved();
    } catch (err: any) {
      console.error('Phone update error:', err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to update phone number. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Update Your Phone Number"
    >
      <form
        onSubmit={handleSubmit}
        className="space-y-4"
        aria-label="Phone number update form"
      >
        {/* Instructional text */}
        <p id="phone-prompt-description" className="text-sm text-gray-600">
          Enter a valid 10-digit phone number for <span className="font-bold text-red-600">{user?.name || user?.given_name || 'your account'}</span>.
          This will be used to contact <span className="font-bold text-red-600">{user?.name || user?.given_name || 'you'}</span> in the event of an emergency.
          Please enter your phone number below.
        </p>

        {/* Error message */}
        {error && (
          <div
            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded"
            role="alert"
          >
            {error}
          </div>
        )}

        {/* Phone input */}
        <div>
          <label
            htmlFor="phoneInput"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Phone Number *
          </label>
          <input
            ref={inputRef}
            type="tel"
            id="phoneInput"
            name="phoneInput"
            value={phoneInput}
            onChange={(e) => {
              setPhoneInput(e.target.value);
              if (error) setError(null);
            }}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="(555) 123-4567"
            disabled={isSubmitting}
            aria-describedby="phone-prompt-description"
            aria-invalid={!!error}
            autoComplete="tel"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
            className="flex-1"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
